import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartAddEvent, CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * The container that owns this quick-add's variant selection: a product card on
   * collection grids, or a `[data-variant-scope]` wrapper where there is no product
   * card (e.g. the Shop the Look hotspot dialog).
   * @returns {Element | null}
   */
  #getSelectionScope() {
    return this.closest('product-card') ?? this.closest('[data-variant-scope]');
  }

  /**
   * Gets the currently selected variant ID from the selection scope. Product cards
   * expose getSelectedVariantId(); a plain scope is read via its checked swatch radio.
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const scope = /** @type {any} */ (this.#getSelectionScope());
    if (!scope) return null;
    if (typeof scope.getSelectedVariantId === 'function') {
      return scope.getSelectedVariantId() || null;
    }
    const checked = /** @type {HTMLInputElement | null} */ (
      scope.querySelector('input[type="radio"]:checked[data-variant-id]')
    );
    return checked?.dataset.variantId || null;
  }

  /** @type {Array<{id:number, available:boolean, options:string[]}> | null} */
  #variantData = null;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));

    // For the inline size quick-add, reflect which sizes (or the OS button) are in
    // stock for the initially selected colour once the swatch state has settled.
    if (this.hasAttribute('data-variants')) {
      requestAnimationFrame(() => this.#updateSizeAvailability());
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const eventScope = event.target.closest('product-card') ?? event.target.closest('[data-variant-scope]');
    if (eventScope !== this.#getSelectionScope()) return;

    // Inline size mode keeps its strip; just resync availability (sizes or OS)
    // for the newly selected colour.
    if (this.hasAttribute('data-variants')) {
      this.#updateSizeAvailability();
      return;
    }

    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Parsed slim variant data ([{ id, available, options }]) for inline size add.
   * @returns {Array<{id:number, available:boolean, options:string[]}>}
   */
  #getVariantData() {
    if (this.#variantData) return this.#variantData;
    try {
      this.#variantData = JSON.parse(this.dataset.variants || '[]');
    } catch {
      this.#variantData = [];
    }
    return this.#variantData;
  }

  /**
   * The option values of the currently selected variant (the chosen swatch colour,
   * or the first available variant as a fallback). Used as the base for swapping in
   * a clicked size while keeping every other option (e.g. colour) fixed.
   * @returns {string[] | null}
   */
  #getBaseOptions() {
    return this.#getCurrentVariant()?.options ?? null;
  }

  /**
   * The currently selected variant (chosen swatch colour, or the first available
   * variant as a fallback). Used directly by the "OS" one-size button.
   * @returns {{id:number, available:boolean, options:string[]} | null}
   */
  #getCurrentVariant() {
    const variants = this.#getVariantData();
    if (!variants.length) return null;

    const selectedId = this.#getSelectedVariantId() || this.dataset.defaultVariantId;
    return (
      variants.find((variant) => String(variant.id) === String(selectedId)) ||
      variants.find((variant) => variant.available) ||
      variants[0] ||
      null
    );
  }

  /**
   * Finds the variant matching the current colour with the given size swapped in.
   * @param {string} size
   * @returns {{id:number, available:boolean, options:string[]} | null}
   */
  #findVariantForSize(size) {
    const variants = this.#getVariantData();
    const sizeIndex = Number(this.dataset.sizeIndex);
    const base = this.#getBaseOptions();
    if (!base || Number.isNaN(sizeIndex)) return null;

    const target = base.slice();
    target[sizeIndex] = size;

    return (
      variants.find(
        (variant) =>
          variant.options.length === target.length && variant.options.every((value, i) => value === target[i])
      ) ?? null
    );
  }

  /**
   * Reflects in-stock state of each size button for the currently selected colour.
   */
  #updateSizeAvailability() {
    const container = this.querySelector('[data-quick-add-sizes]');
    if (!container) return;

    for (const button of container.querySelectorAll('[data-size], [data-os]')) {
      if (!(button instanceof HTMLButtonElement)) continue;
      const match = button.hasAttribute('data-os')
        ? this.#getCurrentVariant()
        : this.#findVariantForSize(button.dataset.size ?? '');
      const available = Boolean(match && match.available);
      button.disabled = !available;
      button.setAttribute('aria-disabled', String(!available));
    }
  }

  /**
   * Adds the chosen size (in the currently selected colour) to the cart by
   * submitting the product form, which opens the cart drawer via CartAddEvent.
   * @param {Event} event
   */
  handleSizeClick = (event) => {
    event.preventDefault();

    const button =
      event.target instanceof Element ? event.target.closest('[data-size], [data-os]') : null;
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    const match = button.hasAttribute('data-os')
      ? this.#getCurrentVariant()
      : this.#findVariantForSize(button.dataset.size ?? '');
    if (!match || !match.available) return;

    const form = this.querySelector('form');
    const variantInput = /** @type {HTMLInputElement | null} */ (form?.querySelector('input[name="id"]'));
    if (!form || !variantInput) return;

    variantInput.value = String(match.id);
    // The hidden id input is disabled when the default variant is unavailable;
    // re-enable it so the chosen variant is submitted.
    variantInput.disabled = false;

    // Open the cart drawer once this add succeeds. The drawer's built-in auto-open
    // is gated behind a theme setting; the inline quick-add should always reveal
    // the cart, so we open it explicitly (no-op if the cart is a page, not a drawer).
    this.#openCartDrawerOnAdd();

    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
    } else {
      form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }
  };

  /**
   * Opens the cart drawer when the next (successful) cart add for this product
   * lands. Scoped to this product and self-cleaning so it never opens the drawer
   * in response to an unrelated add.
   */
  #openCartDrawerOnAdd() {
    const drawer = /** @type {any} */ (document.querySelector('cart-drawer-component'));
    if (!drawer || typeof drawer.open !== 'function') return;

    const productId = /** @type {HTMLElement | null} */ (
      this.querySelector('product-form-component')
    )?.dataset.productId;

    const controller = new AbortController();

    document.addEventListener(
      CartAddEvent.eventName,
      (event) => {
        const detail = /** @type {any} */ (event).detail;
        if (detail?.data?.didError) return;
        if (productId && detail?.data?.productId && String(detail.data.productId) !== String(productId)) return;
        drawer.open();
        controller.abort();
      },
      { signal: controller.signal }
    );

    // Safety net: stop listening if the add never completes.
    setTimeout(() => controller.abort(), 4000);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
